import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import { ButtonRow, FormStepper, FormInput, useTrackedFormik } from '@omnigenbiodata/ui';
import SignatureBlock from '../../../../components/content/SignatureBlock';

import InnerLayout from '../../../../layout/Inner';
import ROUTES from '../../../../core/constants/routes.constant';
import { SAMPLE_STEPS, MESSAGES } from '../../../../core/constants/forms.constant';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { stepForward } from '../../../../store/samples';
import { formValueSelector } from '../../../../store/samples/selectors';
import { participantEnrolmentSelector } from '../../../../store/participant/selectors';

function WitnessSignature() {
  const dispatch = useAppDispatch();
  const values = useAppSelector(formValueSelector);
  const participantEnrolment = useAppSelector(participantEnrolmentSelector);
  const navigateTo = useNavigate();

  const validationSchema = yup.object({
    witnessFirstName: yup
      .string()
      .required(MESSAGES.witnessFirstNameRequired)
      .test('asyncWitnessFirstName', MESSAGES.witnessFirstNameRequired, async (value) => {
        return participantEnrolment?.witnessFirstName?.toUpperCase() === value?.toUpperCase();
      }),
    witnessSurname: yup
      .string()
      .required(MESSAGES.lastNameRequired)
      .test('asyncWitnessFirstName', MESSAGES.witnessLastNameRequired, async (value) => {
        return participantEnrolment?.witnessSurname?.toUpperCase() === value?.toUpperCase();
      }),
    witnessSignature: yup.string().required(MESSAGES.sigRequired),
  });

  const formik = useTrackedFormik(
    {
      initialValues: {
        witnessFirstName: '',
        witnessSurname: '',
        witnessSignature: '',
        ...values,
      },
      validationSchema: validationSchema,
      onSubmit: (values) => {
        dispatch(stepForward(values));
        navigateTo(ROUTES.samplesStaffMemberSignature);
      },
    },
    'samples-witness-signature',
  );

  return (
    <InnerLayout>
      <form onSubmit={formik.handleSubmit}>
        <Box my={3}>
          <FormStepper steps={SAMPLE_STEPS} activeStep={4} />
        </Box>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Witness signature
        </Typography>
        <Typography variant="body1" component="p" align="center" paragraph>
          In instances where the participant electronically signed the consent form with their fingerprint and a
          literate witness co-signed, the same witness must also sign the sample collection form.
        </Typography>
        <Typography variant="body1" component="p" align="center" paragraph>
          Please confirm with the witness they are the same individual who co-signed for the participant at enrolment.
        </Typography>
        <Box mb={8}>
          <FormInput
            error={formik.errors.witnessFirstName}
            name="witnessFirstName"
            label="First name of witness"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.witnessFirstName}
            value={formik.values.witnessFirstName}
          />
          <FormInput
            error={formik.errors.witnessSurname}
            name="witnessSurname"
            label="Surname of witness"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.witnessSurname}
            value={formik.values.witnessSurname}
          />
        </Box>
        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item sm={2} />
            <Grid item sm={8}>
              <SignatureBlock
                error={formik.errors.witnessSignature}
                name="witnessSignature"
                label={`Please ask the witness to confirm they have reviewed the sample barcode labels and that the barcode ID (${values.serumBloodBarcode}) has been correctly entered into the sample collection form. By signing, the witness confirms the sample barcode ID on behalf of the participant.`}
                value={formik.values.witnessSignature}
                touched={formik.touched.witnessSignature}
                setValue={formik.setFieldValue}
              />
            </Grid>
          </Grid>
        </Box>

        <ButtonRow buttonSize="large" forwardColor="primary" />
      </form>
    </InnerLayout>
  );
}

export default WitnessSignature;
